import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import cn from 'classnames';

import {
  CustomButton,
  Spinner,
  CopyButton,
  AppStoreButton,
  QrCode,
  DeviceInfo,
  CustomLink,
} from '../../components';
import { LogoIcon, NuviuLogoIcon, TriangleExclamationIcon } from '../../icons';
import { CONNECTION_TIMEOUT, NUVIU_USER_AGENT, WEB_BLUETOOTH_SAMPLES_LINK } from '../../constants';

import styles from './HomePage.module.scss';

const Home = () => {
  const isBrowserValid = useMemo(() => navigator.userAgent.includes(NUVIU_USER_AGENT), []);
  const timerRef = useRef(null);
  const [error, setError] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [settings, setSettings] = useState({
    connected: false,
    connecting: false,
    bleDevice: null,
  });

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  const updateSettings = (newSettings) => {
    setSettings((settings) => ({ ...settings, ...newSettings }));
  };

  const clearState = () => {
    updateSettings({
      connected: false,
      bleDevice: null,
    });
  };

  const onDisconnected = useCallback(() => {
    console.log('Device disconnected!');
    clearState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectToDevice = () => {
    setError('');

    navigator.bluetooth
      .requestDevice({
        acceptAllDevices: true,
      })
      .then((bluetoothDevice) => {
        timerRef.current = setTimeout(() => {
          setError('The time is up. Please try to connect the device again.');
          setIsConnecting(false);
        }, CONNECTION_TIMEOUT);
        setIsConnecting(true);
        bluetoothDevice.addEventListener('gattserverdisconnected', onDisconnected);
        bluetoothDevice.gatt
          .connect()
          .then(() => {
            clearTimeout(timerRef.current);
            updateSettings({
              connected: true,
              bleDevice: bluetoothDevice,
            });
          })
          .catch((err) => {
            clearTimeout(timerRef.current);
            setError("Can't connect the device. Please try again.");
            console.log(err);
          })
          .finally(() => setIsConnecting(false));
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleDisconnect = () => {
    const { bleDevice } = settings;

    bleDevice.removeEventListener('gattserverdisconnected', onDisconnected);

    if (bleDevice.gatt.connected) {
      bleDevice.gatt.disconnect();
      console.log('Disconnecting from Bluetooth Device...');
    } else {
      console.log('> Bluetooth Device is already disconnected');
    }
    clearState();
  };

  return (
    <div className={styles.homePage}>
      <div className={cn(styles.homePageContent, { [styles.darkBg]: !isBrowserValid })}>
        {isBrowserValid ? (
          <>
            <LogoIcon className={cn(styles.logo, 'animate__animated animate__fadeIn')} />
            <h1 className={cn(styles.title, 'animate__animated animate__fadeIn')}>
              Bluetooth Demo
            </h1>
            <p className={cn(styles.description, 'animate__animated animate__fadeIn')}>
              This sample illustrates the use of Web Bluetooth API to retrieve basic device
              information from a nearby Bluetooth Low Energy Device
            </p>
            <div
              className={cn(
                styles.borderWrap,
                styles.connectionBlockContainer,
                'animate__animated animate__fadeIn',
              )}
            >
              <section className={styles.connectionBlock}>
                {settings.connected ? (
                  <>
                    <CustomButton className={styles.customBtn} onClick={handleDisconnect}>
                      Disconnect
                    </CustomButton>
                    <h4 className={styles.connectionBlockTitle}>Live Output</h4>
                    <DeviceInfo device={settings?.bleDevice} />
                  </>
                ) : (
                  <>
                    <CustomButton className={styles.customBtn} onClick={() => connectToDevice()}>
                      Scan Devices
                    </CustomButton>
                    {error && (
                      <div className={styles.error}>
                        <TriangleExclamationIcon />
                        {error}
                      </div>
                    )}
                  </>
                )}
              </section>
            </div>
            <div
              className={cn(
                styles.borderWrap2,
                styles.examplesLinkContainer,
                'animate__animated animate__fadeIn',
              )}
            >
              <CustomLink href={WEB_BLUETOOTH_SAMPLES_LINK} text="FIND MORE EXAMPLES" />
            </div>
            {isConnecting && (
              <div className={styles.loadingScreen}>
                <Spinner />
              </div>
            )}
          </>
        ) : (
          <>
            <div
              className={cn(styles.bgFigureImgContainer, 'animate__animated animate__fadeInRight')}
            >
              <img className={styles.bgFigureImg} src="/bgFigureImg.png" alt="background figure" />
            </div>
            <div
              className={cn(
                styles.bgFigureTwoImgContainer,
                'animate__animated animate__fadeInLeft',
              )}
            >
              <img
                className={styles.bgFigureTwoImg}
                src="/bgFigureTwoImg.png"
                alt="background figure two"
              />
            </div>
            <NuviuLogoIcon className={cn(styles.nuviuLogo, 'animate__animated animate__fadeIn')} />
            <img
              className={cn(styles.bigNuviuLogo, 'animate__animated animate__fadeIn')}
              src="/bigNuviuLogo.png"
              alt="big logo"
            />
            <h1 className={cn(styles.linkSectionTitle, 'animate__animated animate__fadeIn')}>
              Open this link in <br /> nuviu mobile browser
            </h1>
            <div
              className={cn(
                styles.borderWrap2,
                styles.copyUrlBlockContainer,
                'animate__animated animate__fadeIn',
              )}
            >
              <CopyButton value={window.location.origin} />
            </div>
            <p className={cn(styles.downloadLabel, 'animate__animated animate__fadeIn')}>
              DOWNLOAD NUVIU
            </p>
            <AppStoreButton
              className={cn(styles.appStoreBtn, 'animate__animated animate__fadeIn')}
            />
            <div
              className={cn(
                styles.borderWrap2,
                styles.qrCodeBlockContainer,
                'animate__animated animate__fadeIn',
              )}
            >
              <QrCode imgSrc={process.env.REACT_APP_ENV === 'dev' ? "/testQrCode.svg" : '/qrCode.svg'} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Home;
